import { z } from "zod";

/** Mirrors the Prisma `PostStatus` enum. */
export const postStatusSchema = z.enum(["DRAFT", "PUBLISHED"]);
export type PostStatus = z.infer<typeof postStatusSchema>;

export const SLUG_MAX_LENGTH = 80;

/** Lowercase words joined by single hyphens: `how-to-prep-for-a-panel`. */
export const slugSchema = z
  .string()
  .max(SLUG_MAX_LENGTH)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lowercase letters, numbers and single hyphens");

/** Turn a title into a URL slug (ASCII, hyphenated, trimmed to SLUG_MAX_LENGTH on a word edge). */
export const slugify = (title: string): string => {
  const slug = title
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (slug.length <= SLUG_MAX_LENGTH) return slug;
  const cut = slug.slice(0, SLUG_MAX_LENGTH);
  const lastHyphen = cut.lastIndexOf("-");
  return (lastHyphen > 20 ? cut.slice(0, lastHyphen) : cut).replace(/-+$/, "");
};

/** Normalise free-typed tags: trimmed, lowercased, de-duplicated, max 10. */
export const normalizeTags = (tags: string[]): string[] => {
  const cleaned = tags.map((tag) => tag.trim().toLowerCase().replace(/\s+/g, " ")).filter(Boolean);
  return [...new Set(cleaned)].slice(0, 10);
};

/** A Tiptap document. Node contents are validated by the editor schema at render time. */
export const tiptapDocSchema = z.object({
  type: z.literal("doc"),
  content: z.array(z.record(z.string(), z.unknown())).optional(),
});
export type TiptapDoc = z.infer<typeof tiptapDocSchema>;

export const emptyDoc = (): TiptapDoc => ({ type: "doc", content: [{ type: "paragraph" }] });

/** True when the document has no visible text, image or embed. */
export const isDocEmpty = (doc: TiptapDoc): boolean => {
  const hasContent = (node: Record<string, unknown>): boolean => {
    if (["image", "embed", "widget", "horizontalRule"].includes(node["type"] as string)) {
      return true;
    }
    if (typeof node["text"] === "string" && node["text"].trim() !== "") return true;
    const children = node["content"];
    return Array.isArray(children) && children.some((child) => hasContent(child));
  };
  return !(doc.content ?? []).some((node) => hasContent(node));
};
